import { useMemo, useState } from "react";
import { Pencil, Plus, Trash2 } from "lucide-react";
import api from "../../api";
import { useApi } from "../../hooks/useApi";
import { useToast } from "../../context/ToastContext";
import { getErrorMessage } from "../../utils/apiError";
import { Product, ProductCategory } from "../../types";
import { PRODUCT_TYPES, ZATCA_PHASES, labelOf } from "../../constants/enums";
import PageHeader from "../../components/ui/PageHeader";
import Card from "../../components/ui/Card";
import Button, { IconButton } from "../../components/ui/Button";
import DataTable, { Column, RowActions } from "../../components/ui/DataTable";
import { FilterSelect, SearchInput, Toolbar } from "../../components/ui/Toolbar";
import ConfirmDialog from "../../components/ui/ConfirmDialog";
import Badge, { StatusBadge } from "../../components/ui/Badge";
import ProductFormModal from "./ProductFormModal";

export default function ProductsPage() {
    const toast = useToast();
    const [type, setType] = useState("");
    const [categoryId, setCategoryId] = useState("");
    const [search, setSearch] = useState("");

    const { data, loading, error, reload } = useApi<Product[]>("/product", { type, categoryId });
    const { data: categories } = useApi<ProductCategory[]>("/product-category");

    const [formOpen, setFormOpen] = useState(false);
    const [editing, setEditing] = useState<Product | null>(null);
    const [deleting, setDeleting] = useState<Product | null>(null);

    const rows = useMemo(() => {
        const q = search.trim().toLowerCase();
        if (!data) return null;
        return q
            ? data.filter(
                  (p) =>
                      p.name.toLowerCase().includes(q) ||
                      p.category?.name.toLowerCase().includes(q) ||
                      p.manufacturer?.name.toLowerCase().includes(q)
              )
            : data;
    }, [data, search]);

    const categoryOptions = (categories ?? [])
        .filter((c) => !type || c.type === type)
        .map((c) => ({ value: String(c.id), label: c.name }));

    const openCreate = () => {
        setEditing(null);
        setFormOpen(true);
    };

    const handleDelete = async () => {
        if (!deleting) return;
        try {
            await api.delete(`/product/${deleting.id}`);
            toast.success("Product deleted");
            setDeleting(null);
            reload();
        } catch (err) {
            toast.error(getErrorMessage(err));
        }
    };

    const columns: Column<Product>[] = [
        { key: "name", header: "Product", render: (p) => <span className="font-semibold text-white">{p.name}</span> },
        { key: "type", header: "Type", render: (p) => <StatusBadge value={p.type} /> },
        { key: "category", header: "Category", render: (p) => p.category?.name ?? "—" },
        {
            key: "detail",
            header: "Manufacturer / ZATCA",
            hideOnMobile: true,
            render: (p) =>
                p.type === "HARDWARE" ? (
                    p.manufacturer?.name ?? <span className="text-slate-600">—</span>
                ) : p.zatcaPhase ? (
                    <Badge tone="violet">ZATCA {labelOf(ZATCA_PHASES, p.zatcaPhase)}</Badge>
                ) : (
                    <span className="text-slate-600">—</span>
                ),
        },
        {
            key: "usage",
            header: "Assigned",
            hideOnMobile: true,
            render: (p) => <span className={p._count?.customerProducts ? "text-slate-200 font-semibold" : "text-slate-600"}>{p._count?.customerProducts ?? 0}</span>,
        },
        {
            key: "actions",
            header: "",
            className: "w-24 text-right",
            render: (p) => (
                <RowActions>
                    <IconButton
                        icon={Pencil}
                        label="Edit"
                        onClick={() => {
                            setEditing(p);
                            setFormOpen(true);
                        }}
                    />
                    <IconButton icon={Trash2} label="Delete" tone="danger" onClick={() => setDeleting(p)} />
                </RowActions>
            ),
        },
    ];

    const filtered = !!(type || categoryId || search);

    return (
        <>
            <PageHeader
                title="Products"
                subtitle="Hardware and software you sell to customers."
                actions={
                    <Button icon={Plus} onClick={openCreate}>
                        Add product
                    </Button>
                }
            />

            <Card padded={false}>
                <Toolbar>
                    <SearchInput value={search} onChange={setSearch} placeholder="Search products…" />
                    <FilterSelect
                        value={type}
                        onChange={(v) => {
                            setType(v);
                            setCategoryId("");
                        }}
                        options={PRODUCT_TYPES}
                        allLabel="All types"
                    />
                    <FilterSelect value={categoryId} onChange={setCategoryId} options={categoryOptions} allLabel="All categories" />
                </Toolbar>
                <DataTable
                    columns={columns}
                    rows={rows}
                    loading={loading}
                    error={error}
                    onRetry={reload}
                    rowKey={(p) => p.id}
                    emptyTitle={filtered ? "No matching products" : "No products yet"}
                    emptyText={filtered ? "Try changing the filters." : "Create categories first, then add your products."}
                    emptyAction={
                        !filtered && (
                            <Button icon={Plus} size="sm" onClick={openCreate}>
                                Add product
                            </Button>
                        )
                    }
                />
            </Card>

            <ProductFormModal
                open={formOpen}
                product={editing}
                onClose={() => setFormOpen(false)}
                onSaved={() => {
                    setFormOpen(false);
                    reload();
                }}
            />

            <ConfirmDialog
                open={!!deleting}
                onClose={() => setDeleting(null)}
                onConfirm={handleDelete}
                title="Delete product?"
                message={
                    deleting?._count?.customerProducts ? (
                        <>
                            <b className="text-white">{deleting.name}</b> is assigned to <b className="text-white">{deleting._count.customerProducts}</b>{" "}
                            customer product(s) and can't be deleted until those are removed.
                        </>
                    ) : (
                        <>
                            <b className="text-white">{deleting?.name}</b> will be removed. This can't be undone.
                        </>
                    )
                }
            />
        </>
    );
}